import React, {useEffect, useMemo, useState} from "react";
import {
    CircularProgress,
    Dialog,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import {useNavigate} from "react-router";
import FormRenderer from "../Forms/FormRenderer";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import {Grey6, Grey7, Grey17} from "../../theme/styles";

/**
 * Create a first-class object of any classification.
 *
 * The type drives everything else. Its NumberingStyle decides whether the number is issued by the
 * pool (INTERNAL) or typed by the user (anything else) -- the backend rejects a supplied number for
 * an INTERNAL type and rejects a create with no number for a customer-numbered one, so this form has
 * to send exactly one of the two.
 *
 * When the realm has exactly ONE active type for this classification it is selected silently and the
 * picker is not rendered: a dropdown with a single option asks a question that has no alternative
 * answer. The type is still shown as text, so the user knows what they are creating.
 */

const IDOC = "v1.ObjectRevision-001";
const INTERNAL = "INTERNAL";

type ObjectType = {
    USID?: string;
    Name?: string;
    Acronym?: string;
    NumberingStyle?: string;
    ObjectClassification?: string;
};

/**
 * A FORM the chosen type offers -- a presentation of a shape, not the shape itself. The server lists
 * only RELEASED ones, so anything here can be created against.
 */
type ObjectForm = {
    FormRevisionUSID?: string;
    Name?: string;
    Description?: string;
    DisplayRevision?: string;
    /** The shape this form presents. Null means the form is broken and cannot seed a record. */
    RecordTypeRevisionUSID?: string | null;
};

/** One level of the object type's line, with the forms that level offers. */
type TypeLevel = {
    ObjectTypeUSID: string;
    Name: string;
    Depth: number;
    Forms: ObjectForm[];
    FormCount: number;
    /** Said by the SERVER, not derived here, so the rule for when somebody is asked lives once. */
    Prompts: boolean;
    AutoSelected: ObjectForm | null;
    /** The type's OWN shape (OBJECT_TYPE.RECORD_TYPE_REVISION_USID), which a record takes when no
     *  designed form is chosen for it. Null when the type has none. */
    RecordTypeRevisionUSID?: string | null;
    ShapeName?: string | null;
};

/** The leaf type's own shape, as the server says it: what the record is seeded from when no level
 *  offers a designed form. A Contract's eighteen fields live here, seeded as CONTRACT_FORM and
 *  never wrapped in a form, so without this the dialog asked nothing and the Form tab asked
 *  everything a moment later. */
type TypeShape = {RecordTypeRevisionUSID: string; Name: string | null};

type Props = {
    open: boolean;
    onClose: () => void;
    /**
     * What was just created, so a caller can do something with it.
     *
     * IT CARRIES THE RECORD because the only caller that needed nothing was the list page, which
     * refreshes itself and lands on the draft. A project tab has to FILE what it just made, and
     * filing needs the LOGICAL object USID -- `ObjectUSID`, never the revision's, because a
     * membership survives every Revise and keying it on a revision would unfile the record the
     * first time somebody revised it.
     *
     * Optional and ignored by the existing caller, so this stays the dialog it was.
     */
    onCreated?: (created: {ObjectUSID?: string; RevisionUSID?: string; ObjectID?: string}) => void;
    /**
     * Stay where we are instead of opening the new draft.
     *
     * FOR A CALLER THAT HAS SOMETHING TO DO FIRST. Landing on the record is right from a list --
     * there is nothing else to say -- and wrong from a project tab, which has to file the record
     * and then show it filed. Navigating away mid-flight would leave the filing to finish against
     * a page nobody is looking at, and a failure to report to nobody.
     */
    stayOnCreate?: boolean;
    /** ACCESS_OBJECT_TYPE.CODE — what the record IS, and what the type list is filtered by. */
    classification: string;
    /** "Document", "Purchase Order" — dialog title and messages. */
    singular: string;
    /** Path segment to land on after creating, e.g. "/documents". */
    route: string;
    /**
     * Also ask which OBJECT TYPES this is offered on, and link it to them after creating.
     *
     * For a FORM. The picker here is deliberately the COMPLEMENT of the type picker above: that one
     * chooses what the form IS (classification FORM, which issues its number), this one chooses what
     * it is FOR. Offering FORM types in both would invite a form to be offered on forms.
     */
    offeredOnObjectTypes?: boolean;
    /**
     * Hold the dialog to ONE type of the classification, by acronym, and say so instead of
     * offering a choice. A project's "New product" makes an Item that must be of type PRT-
     * (2026-10-29): offering every item type there would let somebody file a raw material as a
     * product. Absent, every active type is offered as before.
     */
    objectTypeAcronym?: string;
};

export default function CreateObject({open, onClose, onCreated, classification, singular, route,
                                      offeredOnObjectTypes, stayOnCreate, objectTypeAcronym}: Props) {
    const navigate = useNavigate();
    const sessionContext = useSessionContext();
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    const [types, setTypes] = useState<ObjectType[]>([]);
    const [typesLoading, setTypesLoading] = useState<boolean>(true);
    const [typeUSID, setTypeUSID] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [objectId, setObjectId] = useState<string>("");
    // Ownership. A record must belong to SOMETHING or nobody can see it -- not even its creator, who
    // then cannot open it to fix that. Modelled on t5's stewarded dialog: the active persona is
    // preselected, and save is blocked until at least one of the three is set.
    const [positionUSID, setPositionUSID] = useState<string>("");
    const [userGroupUSID, setUserGroupUSID] = useState<string>("");
    const [workGroupUSID, setWorkGroupUSID] = useState<string>("");
    // The forms the chosen TYPE offers. A type has none until somebody designs one, which is the
    // ordinary case today -- an empty list is not an error and the record is then created against
    // the type alone.
    // The types this thing will be OFFERED ON -- see offeredOnObjectTypes. Optional: a form can be
    // linked later from its own "Offered on" tab, and forcing the decision now would mean somebody
    // guessing before the form exists.
    const [offerTypes, setOfferTypes] = useState<ObjectType[]>([]);
    const [offerOn, setOfferOn] = useState<string[]>([]);
    // THE WHOLE LINE, not just the chosen type. An object type may specialise another, and a record
    // of it is asked the questions every type in its line declares -- so the dialog asks about the
    // line and puts a question only where a level offers a real choice.
    const [line, setLine] = useState<TypeLevel[]>([]);
    const [formsLoading, setFormsLoading] = useState<boolean>(false);
    const [shape, setShape] = useState<TypeShape | null>(null);
    const [offersAnyForm, setOffersAnyForm] = useState<boolean>(false);
    // Keyed by object type: what was chosen at each level, including the levels nobody was asked
    // about. See the submit block for why the silent ones are still sent.
    const [chosenForms, setChosenForms] = useState<Record<string, string>>({});
    // THE FORM'S OWN QUESTIONS, ASKED ON CREATE. Ed, 2026-10-27: a contract's fields are form
    // fields on the tenant's contract form rather than columns of their own, and the New Contract
    // dialog draws them -- so the shape the chosen type binds is drawn here, under the record's
    // fixed fields, and its answers are saved against the record the moment it exists. A type that
    // binds no form asks nothing more, which is what every classification did until now.
    const [formValues, setFormValues] = useState<Record<string, any>>({});
    const [formProblems, setFormProblems] = useState<any[]>([]);
    // CLEARED ON EACH OPEN, not on close: answers kept from the last contract would be offered as
    // the next one's, and the one time that is wrong is the time nobody notices.
    useEffect(() => { if (open) { setFormValues({}); setFormProblems([]); } }, [open]);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        const load = async () => {
            setTypesLoading(true);
            setError(null);
            try {
                const response = await api.get("t1/v1/object_types", {
                    params: {ObjectClassification: classification, Active: "true", Limit: 200},
                });
                const list = response?.data?.PlatformMessage?.ListOfObjectType?.ObjectType ?? [];
                const all: ObjectType[] = Array.isArray(list) ? list : [list];
                // HELD TO ONE TYPE when the caller names an acronym -- see objectTypeAcronym.
                const rows = objectTypeAcronym
                    ? all.filter((one: any) => one?.Acronym === objectTypeAcronym) : all;
                if (cancelled) return;

                setTypes(rows);
                // Exactly one type: choose it and never show the picker.
                if (rows.length === 1) setTypeUSID(rows[0].USID ?? "");
                setTypesLoading(false);
            } catch (e) {
                console.error("failed to load object types", e);
                if (!cancelled) {
                    setError(`We could not load the ${singular.toLowerCase()} types. Please try again.`);
                    setTypesLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // `singular` belongs here: the effect reads it to word its error message. Safe to include --
        // it is a string prop that only changes alongside `classification`, which already re-runs
        // this, so nothing reloads more often than before.
    }, [open, api, classification, singular]);

    useEffect(() => {
        if (!open || !offeredOnObjectTypes) return;
        let cancelled = false;

        api.get("t1/v1/object_types", {params: {Active: "true", Limit: 300}})
            .then((response: any) => {
                const list = response?.data?.PlatformMessage?.ListOfObjectType?.ObjectType ?? [];
                const rows: ObjectType[] = Array.isArray(list) ? list : [list];
                if (cancelled) return;
                // EVERYTHING EXCEPT THIS CLASSIFICATION. A form is offered on Documents, Items,
                // Suppliers -- not on other forms.
                setOfferTypes(rows.filter(t => t.ObjectClassification !== classification));
            })
            .catch(() => { if (!cancelled) setOfferTypes([]); });

        return () => { cancelled = true; };
    }, [open, api, offeredOnObjectTypes, classification]);

    // The forms follow the TYPE, so this reloads whenever the type changes -- including the silent
    // preselect when a classification has exactly one type.
    useEffect(() => {
        if (!open || !typeUSID) {
            setLine([]);
            setChosenForms({});
            setShape(null);
            setOffersAnyForm(false);
            return;
        }
        let cancelled = false;

        const load = async () => {
            setFormsLoading(true);
            try {
                const response = await api.get("t1/v1/object_type_line",
                                               {params: {ObjectTypeUSID: typeUSID}});
                const levels: TypeLevel[] = response?.data?.Line ?? [];
                if (cancelled) return;

                setLine(levels);
                setShape(response?.data?.Shape ?? null);
                setOffersAnyForm(Boolean(response?.data?.OffersAnyForm));
                // EXACTLY ONE FORM: choose it and never show a picker, matching how the type picker
                // behaves. A dropdown with a single option asks a question that has no alternative
                // answer. A level with none contributes nothing and is not recorded.
                const preselected: Record<string, string> = {};
                for (const level of levels) {
                    const only = level.AutoSelected?.FormRevisionUSID;
                    if (only) preselected[level.ObjectTypeUSID] = only;
                }
                setChosenForms(preselected);
                setFormsLoading(false);
            } catch (e) {
                // NOT AN ERROR THE USER IS SHOWN. Forms are additive: a type without them still
                // creates records, so a failure here must not block the dialog. It leaves the line
                // empty, which is the same as a type that has none.
                console.error("failed to load the object type line", e);
                if (!cancelled) {
                    setLine([]);
                    setChosenForms({});
                    setShape(null);
                    setOffersAnyForm(false);
                    setFormsLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, api, typeUSID]);

    // Only what the user BELONGS TO. Assigning a record to a position you are not a member of is a
    // stewardship decision, made afterwards on the Visibility tab where the pickers are realm-wide;
    // at creation the honest choice is among your own.
    const account: any = sessionContext?.getAccount?.();
    const myPositions: any[] = Array.isArray(account?.Positions) ? account.Positions : [];
    const myUserGroups: any[] = Array.isArray(account?.UserGroups) ? account.UserGroups : [];
    const myWorkGroups: any[] = Array.isArray(account?.WorkGroups) ? account.WorkGroups : [];

    // THE ONLY OWNER THEY COULD POSSIBLY PICK, when there is exactly one.
    //
    // A picker holding one real option and a "None" is not a choice -- it is a decision already
    // made, presented as work, with an option that would create an unowned record sitting above
    // it. Somebody holding a single position and no groups is the ordinary case in a new tenant.
    // Ed, 2026-09-21, asking for the same treatment the login persona picker just got: "My previous
    // text also applies to the new screen for objects."
    //
    // ACROSS ALL THREE, not per picker. One position AND a user group is a real decision about who
    // owns the record, so every control stays; only a single candidate in total removes it.
    const owners = [
        ...myPositions.map((one: any) => ({kind: 'Position', ...one})),
        ...myUserGroups.map((one: any) => ({kind: 'User Group', ...one})),
        ...myWorkGroups.map((one: any) => ({kind: 'Work Group', ...one})),
    ];
    const soleOwner = owners.length === 1 ? owners[0] : null;

    // SET, NOT JUST DISPLAYED. Save reads these, so showing the owner as text without choosing it
    // would replace a pointless picker with a record that has no owner at all.
    useEffect(() => {
        if (!open || !soleOwner) return;
        if (soleOwner.kind === 'Position') setPositionUSID(soleOwner.USID);
        else if (soleOwner.kind === 'User Group') setUserGroupUSID(soleOwner.USID);
        else setWorkGroupUSID(soleOwner.USID);
    }, [open, soleOwner?.USID, soleOwner?.kind]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Preselect the persona the user is acting as, so the common case is already correct and the
    // pickers are there to override it rather than to fill in.
    useEffect(() => {
        if (!open) return;
        const active: any = sessionContext?.getActiveVisibility?.();
        if (!active?.Value) return;

        const mode = String(active.Mode ?? "");
        if (mode === "User Group" || mode === "UserGroup") setUserGroupUSID(active.Value);
        else if (mode === "Work Group" || mode === "WorkGroup") setWorkGroupUSID(active.Value);
        else if (mode === "Position") setPositionUSID(active.Value);
        // Organization and Management are NOT ownership, so they preselect nothing and the user has
        // to choose -- which is the correct outcome, not an oversight.
    }, [open, sessionContext]);

    // Reset between openings, or a cancelled attempt reappears pre-filled.
    useEffect(() => {
        if (!open) {
            setTypes([]);
            setTypeUSID("");
            setName("");
            setDescription("");
            setObjectId("");
            setOfferOn([]);
            setLine([]);
            setChosenForms({});
            setPositionUSID("");
            setUserGroupUSID("");
            setWorkGroupUSID("");
            setError(null);
            setSaving(false);
        }
    }, [open]);

    const selectedType = types.find(t => t.USID === typeUSID) ?? null;
    // Unknown style is treated as customer-numbered: it is the branch that ASKS rather than the one
    // that assumes, so a misconfigured type produces a question instead of a silently wrong number.
    const isInternal = String(selectedType?.NumberingStyle ?? "").trim().toUpperCase() === INTERNAL;
    const needsNumber = !!selectedType && !isInternal;

    // At least one owner, as in t5. Without it the record is created invisible to everyone.
    const hasOwner = !!positionUSID || !!userGroupUSID || !!workGroupUSID;
    // A PRODUCT NEEDS A DESCRIPTION. Ed, 2026-10-30: on the Product form, Product Name and
    // Description are required, and both are the record's own fields -- so the dialog asks here,
    // and the item door refuses without one (CC-ITEM-PDSC) for anyone who comes another way.
    const descriptionRequired = (selectedType as any)?.Acronym === "PRT-";
    const canSave = !!typeUSID && name.trim().length > 0 && (!needsNumber || objectId.trim().length > 0) && hasOwner && !saving
        && (!descriptionRequired || description.trim().length > 0);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const body: any = {
                ObjectClassification: classification,
                Name: name.trim(),
                Description: description.trim() || null,
                AssignObjectTypeUSID: typeUSID,
            };
            // Sent ONLY for a customer-numbered type. An INTERNAL type rejects a supplied number
            // rather than ignoring it, so sending it unconditionally would fail every create.
            if (needsNumber) body.AssignObjectID = objectId.trim();

            // THE CHOICE OF FORM, never the contract itself. The server resolves the form and sets
            // the record's schema and shape from it, so a client cannot author what it is later
            // judged against -- and it re-resolves the line, so a payload cannot introduce a level,
            // reorder it, or name a form from somewhere else.
            const leaf = line.length ? line[line.length - 1] : null;
            if (leaf && chosenForms[leaf.ObjectTypeUSID]) {
                body.SeededFromRevisionUSID = chosenForms[leaf.ObjectTypeUSID];
            }

            // ONLY WHEN THERE IS A LINE. A type with no parent takes the path it always took, so a
            // tenant that has never built a taxonomy sees no change at all; sending a selection of
            // one would route it through the resolver for nothing.
            //
            // EVERY LEVEL, including the ones nobody was asked about. A level offering exactly one
            // form is never shown, so it is tempting to treat it as derivable from the type later.
            // It is not: release a second form there tomorrow and which form this record used
            // becomes unrecoverable.
            //
            // A JSON STRING, because a list handed to a JSON attribute is dropped by the insert
            // without a word and the attribute reads back as null.
            if (line.length > 1) {
                const selection = line
                    .filter(level => chosenForms[level.ObjectTypeUSID])
                    .map(level => ({ObjectTypeUSID: level.ObjectTypeUSID,
                                    FormRevisionUSID: chosenForms[level.ObjectTypeUSID]}));
                if (selection.length) body.FormSelection = JSON.stringify(selection);
            }

            // Sent only when chosen. The server treats an absent owner as "default from the persona",
            // so sending empty strings would look like a deliberate blank.
            if (positionUSID) body.PrimaryPositionUSID = positionUSID;
            if (userGroupUSID) body.PrimaryUserGroupUSID = userGroupUSID;
            if (workGroupUSID) body.PrimaryWorkGroupUSID = workGroupUSID;

            // The ACTIVE PERSONA. Only the client knows which one is selected, and the server makes
            // it the new record's primary owner -- without it the record is owned by nobody, which
            // means visible to nobody, including whoever just created it.
            const active = sessionContext?.getActiveVisibility();

            const response = await api.post("t1/v1/object_revisions",
                {PlatformMessage: {"-idoc": IDOC, ObjectRevision: body}},
                {headers: {
                    "idoc-request": IDOC,
                    "idoc-response": IDOC,
                    "visibility-mode": active?.Mode ?? "",
                    "visibility-value": active?.Value ?? "",
                }});

            const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
            const rows = Array.isArray(list) ? list : [list];
            const usid = rows[0]?.USID;

            // LINKED TO THE LOGICAL OBJECT, not the revision just created. An association survives
            // every Revise, which is the whole reason a form has revisions -- linking the revision
            // would quietly stop applying the first time somebody revised the form.
            //
            // AFTER the create and reported separately: the form exists either way, and failing the
            // whole creation because one association could not be written would leave somebody with
            // nothing and no obvious reason.
            const formUSID = rows[0]?.ObjectUSID;
            if (formUSID && offerOn.length) {
                const failures: string[] = [];
                for (const objectTypeUSID of offerOn) {
                    try {
                        await api.post("t1/v1/form_links",
                                       {FormUSID: formUSID, ObjectTypeUSID: objectTypeUSID});
                    } catch (e) {
                        failures.push(objectTypeUSID);
                    }
                }
                if (failures.length) {
                    console.error("form created but not linked to", failures);
                }
            }

            // THE ANSWERS, AGAINST THE RECORD THAT NOW EXISTS. A refusal here is reported on the
            // dialog with the record already created: the fields it names are drawn red and the
            // dialog stays open, so the person can correct them -- or close it and find the
            // record, which keeps everything but the refused answers.
            if (usid && Object.keys(formValues).length) {
                try {
                    await api.put("t1/v1/object_form_input",
                                  {ObjectRevisionUSID: usid, Values: formValues});
                } catch (e: any) {
                    const body = e?.response?.data ?? {};
                    setFormProblems(body.Problems ?? []);
                    setError(body.Problems
                        ? `${singular} ${rows[0]?.ObjectID ?? ""} was created, but some of its answers were refused: see the fields marked below.`
                        : (body.Cause ?? `${singular} was created, but its form answers could not be saved.`));
                    onCreated?.({ObjectUSID: rows[0]?.ObjectUSID, RevisionUSID: usid,
                                 ObjectID: rows[0]?.ObjectID});
                    return;
                }
            }

            onCreated?.({ObjectUSID: rows[0]?.ObjectUSID, RevisionUSID: usid,
                         ObjectID: rows[0]?.ObjectID});
            onClose();
            // Land on the new draft. Falling back to the list keeps the dialog from appearing to do
            // nothing if the response shape ever changes.
            if (usid && !stayOnCreate) navigate(`${route}/${usid}`);
        } catch (e: any) {
            console.error("failed to create object", e);
            // The backend owns the numbering and duplicate rules, so surface its reason rather than
            // inventing one.
            const message = e?.response?.data?.Cause || e?.response?.data?.Message;
            setError(message || `We could not create this ${singular.toLowerCase()}. Please try again.`);
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 18, fontWeight: 500, color: Grey17}}>New {singular.toLowerCase()}</DialogTitle>
            <DialogContent>
                {typesLoading && (
                    <div style={{display: "flex", alignItems: "center", gap: 12, padding: "8px 0"}}>
                        <CircularProgress size={18}/>
                        <Typography style={{fontSize: 14, color: Grey7}}>Loading {singular.toLowerCase()} types…</Typography>
                    </div>
                )}

                {/* A realm with no active type for this classification cannot create anything: the
                    type carries the number pool. Said plainly, rather than an empty dropdown. */}
                {!typesLoading && types.length === 0 && (
                    <Typography style={{fontSize: 14, color: Grey7, padding: "8px 0"}}>
                        No active {singular.toLowerCase()} types are configured for your organisation. One is
                        needed before a {singular.toLowerCase()} can be created.
                    </Typography>
                )}

                {!typesLoading && types.length > 0 && (
                    <div style={{display: "flex", flexDirection: "column", gap: 18, paddingTop: 8}}>
                        {/* NAME AND DESCRIPTION FIRST (Ed, 2026-10-28: "When a new Contract or Build
                            Plan dialog is shown, the Name and Description fields should be shown
                            first (at the top)"). They sat below the type, the form choice and the
                            form's own questions, so the two things every record has were the last
                            things asked. The Number stays beside the type that decides whether it
                            is asked at all. */}
                        <TextField
                            label="Name"
                            size="small"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Description"
                            size="small"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            required={descriptionRequired}
                        />

                        {/* Only rendered when there is a genuine choice. */}
                        {types.length > 1 && (
                            <TextField
                                select
                                label="Type"
                                size="small"
                                value={typeUSID}
                                onChange={e => setTypeUSID(e.target.value)}
                                fullWidth
                            >
                                {types.map(t => (
                                    <MenuItem key={t.USID} value={t.USID}>{t.Name}</MenuItem>
                                ))}
                            </TextField>
                        )}
                        {/* WHICH TYPE WAS CHOSEN FOR YOU, when there was only one to choose. Not
                            shown where a second type picker follows: two lines both saying "type",
                            meaning different things -- what this IS versus what it is FOR -- read as
                            a contradiction rather than as information. */}
                        {types.length === 1 && selectedType && !offeredOnObjectTypes && (
                            <Typography style={{fontSize: 13, color: Grey6}}>
                                Type: <span style={{color: Grey17, fontWeight: 500}}>{selectedType.Name}</span>
                            </Typography>
                        )}

                        {/* Shown only for a customer-numbered type. An INTERNAL type issues the number
                            on save, so asking for one would be a field the server refuses. */}
                        {needsNumber && (
                            <TextField
                                label="Number"
                                size="small"
                                value={objectId}
                                onChange={e => setObjectId(e.target.value)}
                                fullWidth
                                helperText="This type does not issue numbers automatically."
                            />
                        )}

                        {/* THE FORM. A type may offer several, and what differs between them is
                            which questions they ask -- so this is a real choice, not a skin. Only
                            rendered when there is more than one; a single form is selected silently
                            and none at all means the record is created against the type alone. */}
                        {/* ONE PICKER PER LEVEL THAT HAS A REAL CHOICE, and nothing at all where
                            there is none. A type may specialise another, and each type's form
                            declares what that type ADDS -- so a record is asked its whole line's
                            questions, and the only question to put to a person is which form a
                            level contributes when that level offers more than one.

                            `Prompts` is the server's answer, not a count compared here, so the two
                            cannot drift over what counts as a choice -- a DEPRECATED form keeps
                            IsCurrent and would otherwise inflate a level to two. */}
                        {line.filter(level => level.Prompts).map(level => (
                            <TextField
                                key={level.ObjectTypeUSID}
                                select
                                label={line.length > 1 ? `Form · ${level.Name}` : "Form"}
                                size="small"
                                value={chosenForms[level.ObjectTypeUSID] ?? ""}
                                onChange={e => setChosenForms(previous => ({
                                    ...previous, [level.ObjectTypeUSID]: e.target.value,
                                }))}
                                helperText={line.length > 1
                                    ? `Which questions ${level.Name} contributes.`
                                    : "The questions this record will be asked."}
                                fullWidth
                            >
                                {level.Forms.map(f => (
                                    <MenuItem key={f.FormRevisionUSID} value={f.FormRevisionUSID}>
                                        {f.Name}{f.DisplayRevision ? ` · Rev ${f.DisplayRevision}` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ))}

                        {/* THE REST OF THE LINE, SAID RATHER THAN HIDDEN. Nobody is asked about a
                            level offering one form, but a record about to be given twenty questions
                            from three ancestors should say where they come from -- "frictionless"
                            is a reason not to ask, not a reason not to tell. Levels contributing
                            nothing are left out, having nothing to say. */}
                        {line.some(level => !level.Prompts && level.AutoSelected) && (
                            <Typography style={{fontSize: 13, color: Grey6}}>
                                {line.length > 1 ? "Also asks: " : "Form: "}
                                {line.filter(level => !level.Prompts && level.AutoSelected)
                                     .map(level => (line.length > 1
                                         ? `${level.Name} (${level.AutoSelected!.Name})`
                                         : level.AutoSelected!.Name))
                                     .map((text, index) => (
                                         <span key={text}>
                                             {index > 0 ? ", " : ""}
                                             <span style={{color: Grey17, fontWeight: 500}}>{text}</span>
                                         </span>
                                     ))}
                            </Typography>
                        )}
                        {formsLoading && (
                            <Typography style={{fontSize: 13, color: Grey7}}>Loading forms…</Typography>
                        )}

                        {/* THE TYPE'S OWN QUESTIONS, when nobody has designed a form for it. Said
                            the same way a form is, so the caption reads "Form: Contract" either
                            way and the person is not told which mechanism is behind it. */}
                        {!formsLoading && !offersAnyForm && shape && (
                            <Typography style={{fontSize: 13, color: Grey6}}>
                                Form: <span style={{color: Grey17, fontWeight: 500}}>{shape.Name ?? "This record's own questions"}</span>
                            </Typography>
                        )}

                        {/* THE QUESTIONS THEMSELVES. One form, auto-selected, with a shape: drawn
                            here so a contract's dates, billing terms and contacts are asked where
                            the contract is made rather than found afterwards on a tab.

                            A DESIGNED FORM WINS; THE TYPE'S OWN SHAPE IS THE FALLBACK. A tenant who
                            designed a form for this type answered the question themselves. One who
                            has not gets the shape the type was seeded with -- which is also what
                            the record is projected against, so the two cannot disagree. */}
                        {(() => {
                            const shaped = line.filter(level => level.AutoSelected?.RecordTypeRevisionUSID);
                            // THE FORM ITSELF, NOT THE SHAPE IT PRESENTS. Until 2026-10-28 this handed
                            // the renderer the form's shape, so a designed form drew as its bare
                            // field list -- every question full width, under no heading -- and the
                            // sections and widths its author placed never reached the dialog. Ed,
                            // beside the source product's dialog: "Is there a way to present the
                            // new contract form like is in the image?" There was: draw the form. A
                            // form revision is an object revision whose schema is its design, and
                            // the definition service draws a record from exactly that document.
                            const form = shaped.length === 1 ? shaped[0].AutoSelected!.FormRevisionUSID ?? null : null;
                            const composite = form ? null
                                : (!offersAnyForm && shape ? shape.RecordTypeRevisionUSID : null);
                            if (!form && !composite) return null;
                            return (
                                <div style={{borderTop: "1px solid #E4E7EC", paddingTop: 12, marginTop: 4}}>
                                    {form ? (
                                        <FormRenderer objectRevisionUSID={form}
                                                      value={formValues}
                                                      onChange={setFormValues}
                                                      problems={formProblems}/>
                                    ) : (
                                        <FormRenderer compositeRevisionUSID={composite!}
                                                      value={formValues}
                                                      onChange={setFormValues}
                                                      problems={formProblems}/>
                                    )}
                                </div>
                            );
                        })()}

                        {/* WHAT THIS FORM IS FOR, as opposed to what it IS. Optional on purpose: the
                            link can be made later from the form's own "Offered on" tab, and forcing
                            the decision now would mean choosing before the form exists. */}
                        {offeredOnObjectTypes && offerTypes.length > 0 && (
                            <FormControl fullWidth size="small">
                                <InputLabel id="offer-on-label">Object Type</InputLabel>
                                <Select
                                    labelId="offer-on-label"
                                    label="Object Type"
                                    multiple
                                    value={offerOn}
                                    onChange={e => setOfferOn(e.target.value as string[])}
                                    renderValue={(selected) => (selected as string[])
                                        .map(id => offerTypes.find(t => t.USID === id)?.Name ?? id)
                                        .join(", ")}
                                >
                                    {offerTypes.map(t => (
                                        <MenuItem key={t.USID} value={t.USID!}>
                                            {t.Name} · {t.ObjectClassification}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    The object types whose records this form fills in. You can change
                                    this later.
                                </FormHelperText>
                            </FormControl>
                        )}

                        {/* Ownership. Each picker offers only what the user belongs to, and the
                            active persona arrives preselected. */}
                        <div>
                            <Typography style={{fontSize: 12, fontWeight: 700, color: Grey6, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10}}>
                                Owner
                            </Typography>
                            <Typography style={{fontSize: 13, color: Grey7, marginBottom: 12}}>
                                Who this {singular.toLowerCase()} belongs to. It decides who can reach it; at least one is
                                required, and you can add more owners afterwards under Visibility.
                            </Typography>
                            <div style={{display: "flex", flexDirection: "column", gap: 14}}>
                                {/* A picker with nothing in it would be a dead control, so each is
                                    rendered only when the user is a member of something -- and a
                                    picker with ONE thing in it is not a choice either, so a lone
                                    candidate is stated rather than offered. */}
                                {soleOwner ? (
                                    <Typography style={{fontSize: 14, color: Grey17}}>
                                        {soleOwner.kind}: <b>{soleOwner.Name}</b>
                                    </Typography>
                                ) : null}
                                {!soleOwner && myPositions.length > 0 && (
                                    <TextField
                                        select
                                        label="Position"
                                        size="small"
                                        value={positionUSID}
                                        onChange={e => setPositionUSID(e.target.value)}
                                        fullWidth
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {myPositions.map((p: any) => (
                                            <MenuItem key={p.USID} value={p.USID}>{p.Name}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {!soleOwner && myUserGroups.length > 0 && (
                                    <TextField
                                        select
                                        label="User Group"
                                        size="small"
                                        value={userGroupUSID}
                                        onChange={e => setUserGroupUSID(e.target.value)}
                                        fullWidth
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {myUserGroups.map((g: any) => (
                                            <MenuItem key={g.USID} value={g.USID}>{g.Name}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {!soleOwner && myWorkGroups.length > 0 && (
                                    <TextField
                                        select
                                        label="Work Group"
                                        size="small"
                                        value={workGroupUSID}
                                        onChange={e => setWorkGroupUSID(e.target.value)}
                                        fullWidth
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {myWorkGroups.map((g: any) => (
                                            <MenuItem key={g.USID} value={g.USID}>{g.Name}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {/* Belonging to nothing is not a validation failure the user can fix
                                    here, so it is explained rather than presented as an empty form. */}
                                {myPositions.length === 0 && myUserGroups.length === 0 && myWorkGroups.length === 0 && (
                                    <Typography style={{fontSize: 13, color: "#8A5B00"}}>
                                        You do not belong to a position, user group or work group, so this
                                        {" "}{singular.toLowerCase()} cannot be given an owner. Ask an administrator to
                                        assign you one first.
                                    </Typography>
                                )}
                            </div>
                        </div>

                        {error && (
                            <Typography style={{fontSize: 13, color: "#B3261E"}}>{error}</Typography>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <CCButton className={css_buttons.whiteStandardButton} onClick={onClose} disabled={saving}>Cancel</CCButton>
                <CCButton className={css_buttons.blueStandardButton} onClick={save} disabled={!canSave}>
                    {saving ? "Creating…" : "Create"}
                </CCButton>
            </DialogActions>
        </Dialog>
    );
}
